/**
 * Layout
 * Please update any global style here!
 */

import { useEffect, useRef, useState } from 'react';
import { LaunchScreen } from '@getgo/chameleon-web-react-wrapper';

import ClientLogo from 'components/client-logo';
import TopPageLoader from 'components/top-page-loader';
import config from 'config';
import { useAppDispatch, useAppSelector } from 'hooks';
import { initiateLoginFlow } from 'lib/auth-provider';
import { addGTMScript, externalScriptObserver } from 'lib/dom-helpers';
import { accountKey, locale, sessionId } from 'modules/global-wrapper';
import { getMeDetails, isSystemUser, meDetailsIsLoading, userKey } from 'modules/iam';
import { paymentMethodsIsLoading, postPaymentMethods } from 'modules/payment-methods';
import {
  isGotoClient,
  isLogmeinClient,
  postSessionDetails,
  sessionClientURL,
  sessionCountry,
  sessionIsLoading,
  sessionProducts,
} from 'modules/session-details';
import Track, { GAEventNames, GATrack, OnePayEventNames } from 'modules/tracking';
import { initializeSegments } from 'modules/tracking/tracking-module';
import { SEARCH_PARAMS } from 'utils/constants';
import CoreView from './core-view';

const CoreContainer = () => {
  const dispatch = useAppDispatch();

  const [isComponentLoading, setComponentLoading] = useState<boolean>(true);

  const isTrackingInitialised = useRef(false);
  const isTrackLandingEvents = useRef(false);

  const globalTheme = localStorage.getItem(SEARCH_PARAMS.theme);
  const selectedAccountKey = useAppSelector(accountKey);
  const selectedGlobalLocale = useAppSelector(locale);
  const selectedGlobalLanguage = localStorage.getItem(SEARCH_PARAMS.locale);

  const selectedSessionIsLoading = useAppSelector(sessionIsLoading);
  const selectedSessionCountry = useAppSelector(sessionCountry);
  const selectedSessionProducts = useAppSelector(sessionProducts);
  const selectedReferralURL = useAppSelector(sessionClientURL);

  const selectedSessionId = useAppSelector(sessionId);
  const selectedSessionClientURL = useAppSelector(sessionClientURL);
  const selectedClientIsLogmein = useAppSelector(isLogmeinClient);
  const selectedClientIsGoto = useAppSelector(isGotoClient);

  const selectedMeDetailsIsLoading = useAppSelector(meDetailsIsLoading);
  const selectedUserKey = useAppSelector(userKey);
  const selectedIsSystemUser = useAppSelector(isSystemUser);

  const selectedPaymentMethodsIsLoading = useAppSelector(paymentMethodsIsLoading);

  if (!isTrackingInitialised.current && selectedAccountKey && selectedUserKey && selectedSessionClientURL) {
    isTrackingInitialised.current = true;

    if (!config.SEGMENTS.disabled) {
      initializeSegments({
        userKey: selectedUserKey,
        accountKey: selectedAccountKey,
        isSystemUser: selectedIsSystemUser,
      });
    }
  }

  if (!isTrackLandingEvents.current && isTrackingInitialised.current && selectedSessionClientURL) {
    isTrackLandingEvents.current = true;

    Track(OnePayEventNames.onePayLanding, { sessionId: selectedSessionId, clientURL: selectedSessionClientURL });
    addGTMScript();
    // Observe any dom modification
    externalScriptObserver.observe(document, { childList: true, subtree: true });
  }

  useEffect(() => {
    if (
      selectedSessionProducts &&
      selectedSessionCountry &&
      selectedGlobalLanguage &&
      selectedGlobalLocale &&
      selectedReferralURL
    ) {
      GATrack(GAEventNames.ViewContent, {
        country: selectedSessionCountry,
        language: selectedGlobalLanguage,
        products: selectedSessionProducts,
        locale: selectedGlobalLocale,
        referrerURL: selectedReferralURL,
      });
    }
  }, [
    selectedGlobalLanguage,
    selectedGlobalLocale,
    selectedReferralURL,
    selectedSessionCountry,
    selectedSessionProducts,
  ]);

  /**
   * Updating the body style for Dark mode.
   * ChameleonThemeProvider isn't working as expected.
   *  */
  useEffect(() => {
    const { body } = document;
    if (body) {
      body.style.backgroundColor = globalTheme === 'dark' ? '#25282d' : '#fff';
      body.style.color = globalTheme === 'dark' ? '#fff' : '#25282d';
    }
  }, [globalTheme]);

  /**
   * Calling all necessary APIs before mounting the routed component
   */
  useEffect(() => {
    const getPaymentAndSessionDetails = async () => {
      try {
        await dispatch(getMeDetails()).unwrap();
        await dispatch(postSessionDetails()).unwrap();
        await dispatch(postPaymentMethods()).unwrap();
        setComponentLoading(false);
      } catch {
        initiateLoginFlow();
      }
    };
    if (selectedAccountKey) {
      getPaymentAndSessionDetails();
    }
  }, [dispatch, selectedAccountKey]);

  const isLoading =
    isComponentLoading || selectedSessionIsLoading || selectedMeDetailsIsLoading || selectedPaymentMethodsIsLoading;

  if (isLoading) {
    if (selectedClientIsGoto || selectedClientIsLogmein) {
      return (
        <LaunchScreen>
          <ClientLogo />
        </LaunchScreen>
      );
    }
    return <TopPageLoader />;
  } else {
    return <CoreView />;
  }
};

export default CoreContainer;
